import React, { Suspense, useEffect, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Layout from '../layouts/Layout';
import Loading from '../components/Loading';
import LogoutSession from '../pages/LogoutSession/LogoutSession';
import Authenticate from '@/pages/Authenticate/Authenticate';
import { useStore } from '@/redux/store';
import ProfileSelection from '@/pages/ProfileSelection/ProfileSelection';
import './App.scss';
import ToDo from '@/pages/ToDo/ToDo';
import {
  HTTPErrorProvider,
  AuthService,
  PageNotFound,
  Unauthorized,
} from 'br2s-ui-common-utils-02';
import Home from '@/pages/Home/Home';
import TermsOfUse from '@/pages/Footer/TermsOfUse/TermsOfUse';
import PrivacyStatement from '@/pages/Footer/PrivacyStatement/PrivacyStatement';
import './App.scss';
import {
  activateSentiments,
  completeWogaaTransaction,
  startWogaaTransaction,
} from '@/services/wogaa/wogaaAnalytics';
import MockSpCp from '@/pages/MockSpCp/MockSpcp';
import { AUTH_SCOPES } from '@/utilities/authScopes';
import SiteMap from '@/pages/SiteMap/SiteMap';
import AccountSettings from '@/pages/Account/Account Settings/AccountSettings';
import SearchResults from '@/pages/SearchResults/SearchResults';
import InstructionRoutes from '@/pages/Instructions/InstructionRoutes';
import Payment from '@/pages/Payment/Payment';
import ProfileSwitchGuard from '@/components/ProfileSwitchGuard/ProfileSwitchGuard';
import ComingSoon from '@/pages/ComingSoon/ComingSoon';
import Overview from '@/pages/Overview/Overview';

type WogaaEventDetail = { trackingId: string; transactionName?: string };

// Lazy loading remote child MFE modules:
export const CommonApp = React.lazy(() => import('common/CommonApp'));
export const ContentModule = React.lazy(() => import('content/ContentModule'));
export const CrmModule = React.lazy(() => import('common/CrmModule'));
export const CneModule = React.lazy(() => import('cne/CneModule'));
export const IpsApiMarketplaceModule = React.lazy(
  () => import('ips/ApiMarketplaceModule'),
);
export const IpsBuyInfoModule = React.lazy(() => import('ips/BuyInfoModule'));
export const IpsCartModule = React.lazy(() => import('ips/CartModule'));
export const IpsVerifyModule = React.lazy(() => import('ips/VerifyModule'));
export const IpsSpecialUenModule = React.lazy(
  () => import('ips/SpecialUenModule'),
);
export const IpsRefundModule = React.lazy(() => import('ips/RefundModule'));
export const RegistryApp = React.lazy(() => import('registry/RegistryApp'));
export const PageLoaderModule = React.lazy(
  () => import('content/PageLoaderModule'),
);
export const SeoLoaderModule = React.lazy(
  () => import('content/SeoLoaderModule'),
);
/**
 * IPS External Payment Page
 *
 * This page serves as the main interface for handling payments for external systems.
 * integrating various payment methods such as Debit/Credit card payments, GooglePay,
 * ApplePay, PayNow and PayPal
 *
 * Example usage:
 * Call URL: /payment?paymentKey="external payment key"
 */
export const IpsExternalPaymentModule = React.lazy(
  () => import('ips/IpsExternalPaymentModule'),
);
/**
 * IPS Payment settings Page
 *
 * This page allows to manage saved payment methods
 * such as credit debit cards.
 */
export const IpsPaymentSettingsModule = React.lazy(
  () => import('ips/IpsPaymentSettingsModule'),
);

function App() {
  const { auth } = useStore();
  const location = useLocation();
  const startTransactionEvent = (e: CustomEvent<WogaaEventDetail>) => {
    startWogaaTransaction(e.detail.trackingId, e.detail.transactionName);
  };

  const completeTransactionEvent = (e: CustomEvent<WogaaEventDetail>) => {
    completeWogaaTransaction(e.detail.trackingId, e.detail.transactionName);
  };

  // Add session ID in session storage
  // for WOGAA sentiments
  useLayoutEffect(() => {
    if (sessionStorage.getItem('wogaaSentimentUserId') !== null) return;

    const id = uuidv4();

    sessionStorage.setItem('wogaaSentimentUserId', id);
  }, []);

  useEffect(() => {
    // script to add wogaa on router change
    const script = document.createElement('script');
    script.setAttribute(
      'src',
      process.env.REACT_APP_WOGAA_ANALYTICS_SCRIPT as string,
    );
    document.head.append(script);
    activateSentiments();

    const startTransactionArgs = [
      '--wogaaStartTransaction--',
      startTransactionEvent as EventListener,
    ] as const;

    const completeTransactionArgs = [
      '--wogaaCompleteTransaction--',
      completeTransactionEvent as EventListener,
    ] as const;

    window.addEventListener(...startTransactionArgs);
    window.addEventListener(...completeTransactionArgs);

    return () => {
      window.removeEventListener(...startTransactionArgs);
      window.removeEventListener(...completeTransactionArgs);
    };
  }, [location, location.pathname]);

  return (
    <HTTPErrorProvider>
      {/* The SeoLoaderModule automatically detects React router location.pathname
          changes to update Helmet props to update the page title/description/keywords meta.
      */}
      <SeoLoaderModule />

      <div className="shell-app">
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* This route enables Magnolia Author preview to go to the content MFE
                routes via the shell in order to render and display various content
                MFE modules in the Magnolia Author preview iFrame.
             */}
            <Route path="/content-preview/*" element={<ContentModule />} />

            <Route path="/*" element={<Layout />}>
              <Route path="*" element={<ProfileSwitchGuard />}>
                <Route path="registry/*" element={<RegistryApp />} />
                <Route path="apimarketplace">
                  <Route path="" element={<Overview pageName="subscribe" />} />
                  <Route path="*" element={<IpsApiMarketplaceModule />} />
                </Route>
                <Route path="cart/*" element={<IpsCartModule />} />
                <Route path="buy-info/*" element={<IpsBuyInfoModule />} />
                <Route path="verify/*" element={<IpsVerifyModule />} />
                <Route path="special-uen/*" element={<IpsSpecialUenModule />} />
                <Route path="refund/*" element={<IpsRefundModule />} />
                <Route path="common/*" element={<CommonApp />} />
                <Route path="crm/*" element={<CrmModule />} />
                <Route
                  path="payment/*"
                  element={<IpsExternalPaymentModule />}
                />
                <Route
                  path="payment-settings/*"
                  element={<IpsPaymentSettingsModule />}
                />
                <Route
                  path="gwf/*"
                  element={
                    <AuthService.AuthGuard
                      scopeId={AUTH_SCOPES.GENERIC_WEBFORM_ADD}
                      type="module"
                      element={<CneModule />}
                    />
                  }
                />
                <Route path="" element={<Home />} />
                <Route path="home" element={<Home />} />
                <Route
                  path="dashboard/*"
                  element={
                    <AuthService.AuthGuard
                      scopeId={AUTH_SCOPES.TODO_READ}
                      type="module"
                      element={<ToDo />}
                    />
                  }
                />
                <Route
                  path="overview/buy-information"
                  element={<Overview pageName="buy-information" />}
                />
                <Route path="search" element={<SearchResults />} />
                <Route path="instruction/*" element={<InstructionRoutes />} />
                <Route
                  path="payment/:enforcementId"
                  element={
                    <AuthService.AuthGuard
                      scopeId={AUTH_SCOPES.ENFORCEMENT_PAYMENT_READ}
                      type="module"
                      element={<Payment />}
                    />
                  }
                />
                <Route
                  path="page-loader-test"
                  element={<PageLoaderModule pagePath={'/page-loader-test'} />}
                />
                <Route
                  path="profile-selection/*"
                  element={
                    <AuthService.AuthGuard
                      scopeId={AUTH_SCOPES.SWITCHPROFILE_READ}
                      type="module"
                      element={<ProfileSelection />}
                    />
                  }
                />

                <Route path="myprofile" element={<AccountSettings />} />
                <Route
                  path=":loginType/oidc/authenticate"
                  element={<Authenticate />}
                />
                <Route path="terms-of-use" element={<TermsOfUse />} />
                <Route
                  path="privacy-statement"
                  element={<PrivacyStatement />}
                />
                <Route path="sitemap" element={<SiteMap />} />
                <Route path="temporary-unavailable" element={<ComingSoon />} />
                <Route path="*" element={<PageNotFound />} />
              </Route>
              {!auth.authenticated && (
                <>
                  <Route
                    path="logout"
                    element={<LogoutSession type="logout" />}
                  />
                  <Route
                    path="sessionTimeout"
                    element={<LogoutSession type="session" />}
                  />
                </>
              )}
              <Route path="unauthorized" element={<Unauthorized />} />
            </Route>
            {process.env.REACT_APP_SHOW_MOCK_SPCP?.toString() === 'true' &&
              !auth.authenticated && (
                <Route path="mockspcp" element={<MockSpCp />} />
              )}
          </Routes>
        </Suspense>
      </div>
    </HTTPErrorProvider>
  );
}

export default App;
